import gsap from 'gsap';

/*
 * Carrousel en profondeur (section .evenements)
 *
 * Port en JavaScript « vanilla » du composant DepthCarousel de React Bits.
 * Les cartes (.depth-carousel__card) sont écrites dans le HTML ; le JS les
 * place dans l'espace 3D, ajoute les flèches et les points, et gère le drag,
 * le clavier et l'autoplay.
 *
 * Options (attributs data- sur l'élément) :
 *   data-card-width, data-card-height, data-radius, data-tint
 *   data-depth, data-spread, data-tilt, data-tilt-direction ("left" | "right")
 *   data-perspective, data-visible-cards, data-falloff, data-blur
 *   data-duration (ms), data-ease (easing GSAP)
 *   data-autoplay, data-autoplay-delay (ms)
 *   data-loop="false" pour désactiver la boucle
 *   data-controls="false" / data-indicators="false" pour cacher flèches / points
 *   data-wheel pour activer la molette (désactivée par défaut, voir plus bas)
 */

const DEFAULTS = {
  cardWidth: 300,
  cardHeight: 380,
  radius: 18,
  tint: '#05060a',
  depth: 220,
  spread: 90,
  tilt: 22,
  tiltDirection: 'right',
  perspective: 1400,
  visibleCards: 4,
  falloff: 0.2,
  blur: 6,
  duration: 1200,
  ease: 'power3.out',
  autoplayDelay: 3200,
};

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

export default class DepthCarousel {
  constructor(element) {
    this.element = element;
    this.stage = element.querySelector('.depth-carousel__stage');
    this.cards = Array.from(element.querySelectorAll('.depth-carousel__card'));
    this.count = this.cards.length;

    if (!this.stage || !this.count) {
      console.warn(
        'DepthCarousel : il faut une div .depth-carousel__stage contenant des .depth-carousel__card.',
      );
      return;
    }

    this.pos = 0; // position décimale pendant l'animation
    this.focus = 0; // index de la carte au premier plan
    this.scale = 1; // réduction selon la taille du conteneur
    this.tween = null;
    this.drag = null;
    this.wheelTimer = null;
    this.autoTimer = null;
    this.hovered = false;
    this.focused = false;
    this.visible = true;
    this.justDragged = false;
    this.reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    this.setOptions();
    this.init();
  }

  /* ---------- Options ---------- */

  setOptions() {
    const d = this.element.dataset;
    const num = (key) =>
      key in d && d[key] !== '' ? Number(d[key]) : DEFAULTS[key];
    const str = (key) => (key in d && d[key] !== '' ? d[key] : DEFAULTS[key]);
    const bool = (key, fallback) => (key in d ? d[key] !== 'false' : fallback);

    this.options = {
      cardWidth: num('cardWidth'),
      cardHeight: num('cardHeight'),
      radius: num('radius'),
      tint: str('tint'),
      depth: num('depth'),
      spread: num('spread'),
      tilt: num('tilt'),
      tiltDirection: str('tiltDirection'),
      perspective: num('perspective'),
      visibleCards: num('visibleCards'),
      falloff: num('falloff'),
      blur: num('blur'),
      duration: num('duration'),
      ease: str('ease'),
      autoplay: bool('autoplay', false),
      autoplayDelay: num('autoplayDelay'),
      loop: bool('loop', true),
      controls: bool('controls', true),
      indicators: bool('indicators', true),
      // La molette est désactivée par défaut : sinon elle bloquerait le
      // défilement de la page (ScrollSmoother) quand la souris est sur le carrousel.
      wheel: bool('wheel', false),
    };
  }

  init() {
    const o = this.options;

    // Variables CSS lues par depth-carousel.scss
    this.element.style.setProperty('--dc-perspective', `${o.perspective}px`);
    this.element.style.setProperty('--dc-card-width', `${o.cardWidth}px`);
    this.element.style.setProperty('--dc-card-height', `${o.cardHeight}px`);
    this.element.style.setProperty('--dc-radius', `${o.radius}px`);

    this.element.setAttribute('role', 'group');
    this.element.setAttribute('aria-roledescription', 'carousel');
    if (!this.element.hasAttribute('aria-label')) {
      this.element.setAttribute('aria-label', 'Carrousel');
    }
    this.element.tabIndex = 0;

    this.computeShift();
    this.createCards();
    if (o.controls && this.count > 1) this.createControls();
    if (o.indicators && this.count > 1) this.createDots();

    this.bindEvents();
    this.updateScale();
    this.layout(this.pos);
    this.updateActive();
    this.initAutoplay();
  }

  /* ---------- Construction du DOM ---------- */

  createCards() {
    this.overlays = this.cards.map((card, i) => {
      const tint = document.createElement('span');
      tint.className = 'depth-carousel__tint';
      tint.style.background = this.options.tint;
      card.appendChild(tint);

      card.setAttribute('aria-roledescription', 'slide');
      card.setAttribute('aria-label', `${i + 1} sur ${this.count}`);
      card.addEventListener('click', () => this.onCardClick(i));

      const img = card.querySelector('img');
      if (img) img.draggable = false;

      return tint;
    });
  }

  createControls() {
    const arrow = (direction, label, path) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `depth-carousel__arrow depth-carousel__arrow--${direction}`;
      button.setAttribute('aria-label', label);
      button.innerHTML = `
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d="${path}" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>`;
      return button;
    };

    this.prevButton = arrow('prev', 'Évènement précédent', 'M15 5l-7 7 7 7');
    this.nextButton = arrow('next', 'Évènement suivant', 'M9 5l7 7-7 7');
    this.prevButton.addEventListener('click', () => this.prev());
    this.nextButton.addEventListener('click', () => this.next());
    this.element.append(this.prevButton, this.nextButton);
  }

  createDots() {
    this.dotsWrap = document.createElement('div');
    this.dotsWrap.className = 'depth-carousel__dots';
    this.dotsWrap.setAttribute('role', 'tablist');
    this.dotsWrap.setAttribute('aria-label', 'Évènements');

    this.dots = this.cards.map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'depth-carousel__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Aller à l'évènement ${i + 1}`);
      dot.addEventListener('click', () => this.setFocus(i, true));
      this.dotsWrap.appendChild(dot);
      return dot;
    });

    this.element.appendChild(this.dotsWrap);
  }

  /* ---------- Mise en page 3D ---------- */

  // La pile se déploie d'un seul côté : on la décale pour que l'ensemble
  // (carte du devant + dernière carte visible) soit centré dans le conteneur,
  // au lieu de déborder du côté où elle s'ouvre. On en déduit aussi la
  // largeur réellement occupée (this.extent), utilisée pour la mise à l'échelle.
  computeShift() {
    const o = this.options;
    const dir = o.tiltDirection === 'left' ? -1 : 1;
    const half = o.cardWidth / 2;
    // Perspective : une carte reculée de `depth` px est réduite d'un facteur k
    const k = o.perspective / (o.perspective + o.depth * o.visibleCards);
    const far = o.spread * o.visibleCards;

    // Bords gauche / droit de la pile pour un décalage s (pile ouverte vers la droite)
    const edges = (s) => ({
      left: Math.min(s - half, (s + far) * k - half * k),
      right: Math.max(s + half, (s + far) * k + half * k),
    });

    let s = 0;
    let { left, right } = edges(s);
    s -= (left + right) / 2; // recentre la pile
    ({ left, right } = edges(s));

    this.shift = dir * s;
    this.extent = right - left;
  }

  layout(pos) {
    const o = this.options;
    const n = this.count;
    const dir = o.tiltDirection === 'left' ? -1 : 1;

    for (let i = 0; i < n; i++) {
      const el = this.cards[i];

      let d = i - pos;
      if (o.loop && n > 1) {
        d = ((d % n) + n) % n;
        if (d > n / 2) d -= n;
      }

      const back = Math.max(0, d);
      const shown = Math.abs(d) <= o.visibleCards + 0.5;

      const tz = -o.depth * d;
      const tx = this.shift + dir * o.spread * d;
      const ry = dir * o.tilt * clamp(d, 0, 1);

      let opacity = d < 0 ? Math.max(0, 1 + d) : 1;
      if (!shown) opacity = 0;

      const brightness = Math.max(0.15, 1 - back * o.falloff);
      const blurPx =
        o.blur > 0
          ? Math.min(o.blur, (back / Math.max(1, o.visibleCards)) * o.blur)
          : 0;

      el.style.transform = `translate(-50%, -50%) scale(${this.scale}) translateX(${tx.toFixed(2)}px) translateZ(${tz.toFixed(2)}px) rotateY(${ry.toFixed(3)}deg)`;
      el.style.opacity = opacity.toFixed(3);
      el.style.filter = `brightness(${brightness.toFixed(3)}) blur(${blurPx.toFixed(2)}px)`;
      el.style.zIndex = String(Math.round(2000 - d * 20));
      el.style.pointerEvents = shown && opacity > 0.05 ? 'auto' : 'none';

      this.overlays[i].style.opacity = clamp(
        back * o.falloff * 1.25,
        0,
        0.86,
      ).toFixed(3);
    }
  }

  // Réduit la pile si le conteneur est trop petit (largeur ET hauteur)
  updateScale() {
    const o = this.options;
    const { width, height } = this.element.getBoundingClientRect();
    if (!width || !height) return;

    const neededW = this.extent + 40; // + marge pour l'ombre et le flou
    const neededH = o.cardHeight + 80; // + place pour les flèches
    this.scale = clamp(Math.min(width / neededW, height / neededH), 0.4, 1);
    this.layout(this.pos);
  }

  /* ---------- Navigation ---------- */

  tweenTo(target, animate) {
    if (this.tween) this.tween.kill();
    const o = this.options;
    const proxy = { p: this.pos };
    const duration = animate && !this.reduceMotion ? o.duration / 1000 : 0;

    this.tween = gsap.to(proxy, {
      p: target,
      duration,
      ease: o.ease,
      onUpdate: () => {
        this.pos = proxy.p;
        this.layout(this.pos);
      },
      onComplete: () => {
        this.pos = ((this.pos % this.count) + this.count) % this.count;
        this.layout(this.pos);
      },
    });
  }

  setFocus(rawIndex, animate = true) {
    const o = this.options;
    const n = this.count;
    const index = o.loop ? ((rawIndex % n) + n) % n : clamp(rawIndex, 0, n - 1);

    let delta = index - this.pos;
    if (o.loop && n > 1) {
      delta = ((delta % n) + n) % n;
      if (delta > n / 2) delta -= n;
    }

    this.tweenTo(this.pos + delta, animate);

    if (index !== this.focus) {
      this.focus = index;
      this.updateActive();
    }
  }

  next() {
    this.setFocus(this.focus + 1, true);
  }

  prev() {
    this.setFocus(this.focus - 1, true);
  }

  updateActive() {
    this.cards.forEach((card, i) => {
      card.setAttribute('aria-hidden', String(i !== this.focus));
      card.classList.toggle('is-active', i === this.focus);
    });

    if (this.dots) {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.focus);
        dot.setAttribute('aria-selected', String(i === this.focus));
      });
    }

    // Sans boucle, on désactive les flèches aux extrémités
    if (!this.options.loop && this.prevButton) {
      this.prevButton.disabled = this.focus === 0;
      this.nextButton.disabled = this.focus === this.count - 1;
    }

    this.element.dispatchEvent(
      new CustomEvent('depthcarousel:change', {
        detail: { index: this.focus, card: this.cards[this.focus] },
      }),
    );
  }

  /* ---------- Événements ---------- */

  bindEvents() {
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerEnd = this.onPointerEnd.bind(this);

    this.element.addEventListener('pointerdown', this.onPointerDown);
    this.element.addEventListener('pointermove', this.onPointerMove);
    this.element.addEventListener('pointerup', this.onPointerEnd);
    this.element.addEventListener('pointercancel', this.onPointerEnd);
    this.element.addEventListener('keydown', (event) => this.onKeyDown(event));

    if (this.options.wheel) {
      this.element.addEventListener('wheel', (event) => this.onWheel(event), {
        passive: false,
      });
    }

    this.resizeObserver = new ResizeObserver(() => this.updateScale());
    this.resizeObserver.observe(this.element);
  }

  onPointerDown(event) {
    if (this.count < 2) return;
    // Les flèches et les points gèrent leur propre clic
    if (event.target.closest('.depth-carousel__arrow, .depth-carousel__dots')) {
      return;
    }
    if (this.tween) this.tween.kill();

    this.drag = {
      x: event.clientX,
      startPos: this.pos,
      lastX: event.clientX,
      lastT: performance.now(),
      v: 0,
      moved: false,
      id: event.pointerId,
    };
  }

  onPointerMove(event) {
    const drag = this.drag;
    if (!drag) return;

    const stepPx = Math.max(this.options.cardWidth * 0.55 * this.scale, 40);
    const dx = event.clientX - drag.x;

    if (!drag.moved && Math.abs(dx) > 4) {
      drag.moved = true;
      this.element.setPointerCapture(drag.id);
    }
    if (!drag.moved) return;

    const now = performance.now();
    const dt = Math.max(now - drag.lastT, 1);
    drag.v = (event.clientX - drag.lastX) / dt;
    drag.lastX = event.clientX;
    drag.lastT = now;

    this.pos = drag.startPos - dx / stepPx;
    this.layout(this.pos);
  }

  onPointerEnd() {
    const drag = this.drag;
    if (!drag) return;
    this.drag = null;
    if (!drag.moved) return;

    // Évite qu'un clic parasite à la fin du drag change de carte
    this.justDragged = true;
    setTimeout(() => (this.justDragged = false), 0);

    const stepPx = Math.max(this.options.cardWidth * 0.55 * this.scale, 40);
    const projected = this.pos - (drag.v * 180) / stepPx;
    this.setFocus(Math.round(projected), true);
  }

  onWheel(event) {
    if (this.count < 2) return;
    event.preventDefault();
    if (this.tween) this.tween.kill();

    const raw =
      Math.abs(event.deltaX) > Math.abs(event.deltaY)
        ? event.deltaX
        : event.deltaY;
    const delta = event.deltaMode === 1 ? raw * 24 : raw;
    const step = clamp(delta / (this.options.cardWidth * 0.9), -0.6, 0.6);

    this.pos += step;
    this.layout(this.pos);

    clearTimeout(this.wheelTimer);
    this.wheelTimer = setTimeout(
      () => this.setFocus(Math.round(this.pos), true),
      130,
    );
  }

  onKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
  }

  onCardClick(index) {
    if (this.justDragged) return;
    this.setFocus(index, true);
  }

  /* ---------- Autoplay ---------- */

  initAutoplay() {
    if (!this.options.autoplay || this.reduceMotion || this.count < 2) return;

    // Pause au survol, au focus, pendant un drag et quand la section n'est pas à l'écran
    this.element.addEventListener('mouseenter', () => (this.hovered = true));
    this.element.addEventListener('mouseleave', () => (this.hovered = false));
    this.element.addEventListener('focusin', () => (this.focused = true));
    this.element.addEventListener('focusout', () => (this.focused = false));

    this.visibilityObserver = new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
    });
    this.visibilityObserver.observe(this.element);

    this.autoTimer = window.setInterval(
      () => {
        if (!this.hovered && !this.focused && this.visible && !this.drag) {
          this.next();
        }
      },
      Math.max(this.options.autoplayDelay, 600),
    );
  }

  destroy() {
    if (this.tween) this.tween.kill();
    clearTimeout(this.wheelTimer);
    clearInterval(this.autoTimer);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
  }
}
